import React, { useContext, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import * as Components from "../../components/components"
import { getUmsatzsteuerCodesOptions, getUmsatzsteuerOptions, getUnitsCodes } from "../../constants"
import { ConfigContext } from "../../contexts/CustomConfigContext"
import { Position } from "../../models/position"
import { FeatureService } from "../../services/FeatureService"
import { PositionListView } from "./PositionListView"
import { PositionSonstigeView } from "./PositionSonstigeView"
import { PositionZusatzinformationListView } from "./PositionZusatzinformationListView"

interface PositionViewProps {
    index: number
    position: Position
    path: string
    onRemoveFactory: (idx: number) => () => void
    onChangeFactory: (idx: number) => (propertyname: string) => (value: any) => void
    umsatzsteuerFixedValue?: number
    umsatzsteuerFixedCode?: string
}

export function PositionView({ index, position, path, onRemoveFactory, onChangeFactory, umsatzsteuerFixedValue, umsatzsteuerFixedCode }: PositionViewProps) {
    const { t } = useTranslation()
    // BT-131 = BT-146 * BT-129
    const hatUnterpositionen = position.unterPositionen.length > 0
    const title = t("rechnung.positionen.postionTitle") + " " + position.positionsnummer
    const unitOptions = getUnitsCodes()
    const umsatzsteuerCodesOptions = getUmsatzsteuerCodesOptions()
    const umsatzsteuerOptions = getUmsatzsteuerOptions()
    const classpath = path.replaceAll(".", "_")
    const customConfigContext = useContext(ConfigContext)

    const onChange = useMemo(() => onChangeFactory(index), [onChangeFactory, index])
    const onRemove = useMemo(() => onRemoveFactory(index), [onRemoveFactory, index])

    useEffect(() => {
        if (umsatzsteuerFixedValue !== undefined) {
            onChange("umsatzsteuer")(umsatzsteuerFixedValue)
        }
    }, [onChange, umsatzsteuerFixedValue])

    useEffect(() => {
        if (umsatzsteuerFixedCode !== undefined) {
            onChange("umsatzsteuerCode")(umsatzsteuerFixedCode)
        }
    }, [onChange, umsatzsteuerFixedCode])

    useEffect(() => {
        onChange("preisbasisEinheit")(position.einheit)
    }, [onChange, position.einheit])

    useEffect(() => {
        if (hatUnterpositionen) {
            onChange("menge")(1)
            onChange("einzelpreisNettoMitRabatt")(position.gesamtpreisNetto)
        }
    }, [onChange, position.gesamtpreisNetto, hatUnterpositionen])

    return (
        <div>
            <Components.Collapsible title={title} collapsed={false} className={index === 0 ? "mt-0 " + classpath : classpath} onRemove={onRemove}>
                <Components.FormGroup label={t("rechnung.positionen.positionsnummer.label")}>
                    <Components.TextEdit
                        placeholder={t("rechnung.positionen.positionsnummer.placeholder")}
                        onChange={onChange("positionsnummer")}
                        value={position.positionsnummer}
                    />
                </Components.FormGroup>
                <Components.FormGroup label={t("rechnung.positionen.bestellreferenz.label")}>
                    <Components.TextEdit
                        placeholder={t("rechnung.positionen.bestellreferenz.placeholder")}
                        onChange={onChange("bestellreferenz")}
                        value={position.bestellreferenz}
                    />
                </Components.FormGroup>
                <Components.FormGroup label={t("rechnung.positionen.artikelnummerVerkaeufer.label")}>
                    <Components.TextEdit
                        placeholder={t("rechnung.positionen.artikelnummerVerkaeufer.placeholder")}
                        onChange={onChange("artikelnummerVerkaeufer")}
                        value={position.artikelnummerVerkaeufer}
                    />
                </Components.FormGroup>
                <Components.FormGroup label={t("rechnung.positionen.artikelbezeichnung.label")}>
                    <Components.TextEdit
                        placeholder={t("rechnung.positionen.artikelbezeichnung.placeholder")}
                        onChange={onChange("artikelbezeichnung")}
                        value={position.artikelbezeichnung}
                    />
                </Components.FormGroup>
                <Components.FormGroup label={t("rechnung.positionen.artikelbeschreibung.label")}>
                    <Components.TextEdit
                        placeholder={t("rechnung.positionen.artikelbeschreibung.placeholder")}
                        onChange={onChange("artikelbeschreibung")}
                        value={position.artikelbeschreibung}
                    />
                </Components.FormGroup>
                {!hatUnterpositionen && (
                    <>
                        <Components.FormGroup label={t("rechnung.positionen.einzelpreisNettoOhneRabatt.label")}>
                            <Components.NumberEdit
                                onChange={onChange("einzelpreisNettoOhneRabatt")}
                                value={position.einzelpreisNettoOhneRabatt}
                                decimalPrecision={customConfigContext?.decimalPrecisionForAmount}
                            />
                        </Components.FormGroup>
                        <Components.FormGroup label={t("rechnung.positionen.einzelpreisNettoMitRabatt.label")}>
                            <Components.NumberEdit
                                onChange={onChange("einzelpreisNettoMitRabatt")}
                                value={position.einzelpreisNettoMitRabatt}
                                decimalPrecision={customConfigContext?.decimalPrecisionForAmount}
                            />
                        </Components.FormGroup>
                        <Components.Row className="mt-0">
                            <Components.Col size={9} sizeSmallDown={12} className="offset-md-3">
                                <Components.Row className="mt-0">
                                    <Components.Col size={6} sizeSmallDown={12}>
                                        <Components.FormGroup label={t("rechnung.positionen.preisbasisMenge.label")} inline={true} labelClassName="small">
                                            <Components.NumberEdit
                                                onChange={onChange("preisbasisMenge")}
                                                value={position.preisbasisMenge}
                                                decimalPrecision={customConfigContext?.decimalPrecisionForAmount}
                                                suffix=""
                                                allowNegative={false}
                                            />
                                        </Components.FormGroup>
                                    </Components.Col>
                                    <Components.Col size={6} sizeSmallDown={12}>
                                        <Components.FormGroup label={t("rechnung.positionen.preisbasisEinheit.label")} inline={true} labelClassName="small">
                                            <Components.MultiSelect
                                                placeholder={t("rechnung.positionen.preisbasisEinheit.placeholder")}
                                                onChange={onChange("preisbasisEinheit")}
                                                value={unitOptions.find((o) => o.value === position.preisbasisEinheit)}
                                                options={unitOptions}
                                                readonly={true}
                                            />
                                        </Components.FormGroup>
                                    </Components.Col>
                                </Components.Row>
                            </Components.Col>
                        </Components.Row>
                        <Components.Row className="mt-0">
                            <Components.Col size={9} sizeSmallDown={12} className="offset-md-3">
                                <Components.Row className="mt-0">
                                    <Components.Col size={6} sizeSmallDown={12}>
                                        <Components.FormGroup label={t("rechnung.positionen.menge.label")} inline={true} labelClassName="small">
                                            <Components.NumberEdit
                                                onChange={onChange("menge")}
                                                value={position.menge}
                                                decimalPrecision={customConfigContext?.decimalPrecisionForAmount}
                                                suffix=""
                                            />
                                        </Components.FormGroup>
                                    </Components.Col>
                                    <Components.Col size={6} sizeSmallDown={12}>
                                        <Components.FormGroup label={t("rechnung.positionen.einheit.label")} inline={true} labelClassName="small">
                                            <Components.MultiSelect
                                                placeholder={t("rechnung.positionen.einheit.placeholder")}
                                                onChange={onChange("einheit")}
                                                value={unitOptions.find((o) => o.value === position.einheit)}
                                                options={unitOptions}
                                            />
                                        </Components.FormGroup>
                                    </Components.Col>
                                </Components.Row>
                            </Components.Col>
                        </Components.Row>

                        <Components.FormGroup label={t("rechnung.positionen.rabattGesamt.label")}>
                            <Components.NumberEdit
                                onChange={onChange("rabattGesamt")}
                                value={position.rabattGesamt}
                                decimalPrecision={customConfigContext?.decimalPrecisionForAmount}
                            />
                        </Components.FormGroup>
                    </>
                )}
                <Components.FormGroup label={t("rechnung.positionen.gesamtpreisNetto.label")}>
                    <Components.NumberEdit value={position.gesamtpreisNetto} readonly />
                </Components.FormGroup>

                <Components.FormGroup label={t("rechnung.positionen.umsatzsteuerCode.label")}>
                    <Components.MultiSelect
                        placeholder={t("rechnung.positionen.umsatzsteuerCode.placeholder")}
                        onChange={onChange("umsatzsteuerCode")}
                        value={umsatzsteuerCodesOptions.find((o) => o.value === position.umsatzsteuerCode)}
                        options={umsatzsteuerCodesOptions}
                        readonly={umsatzsteuerFixedCode !== undefined}
                    />
                </Components.FormGroup>

                {FeatureService.isFeatureActive("DEMO") ? (
                    <Components.FormGroup label={t("rechnung.positionen.umsatzsteuer.label")}>
                        <Components.MultiSelect
                            value={umsatzsteuerOptions.find((o) => o.value === (position.umsatzsteuer ? position.umsatzsteuer.toString() : ""))}
                            options={umsatzsteuerOptions}
                            onChange={onChange("umsatzsteuer")}
                        />
                    </Components.FormGroup>
                ) : (
                    <Components.FormGroup label={t("rechnung.positionen.umsatzsteuer.label")}>
                        <Components.NumberEdit
                            onChange={onChange("umsatzsteuer")}
                            value={position.umsatzsteuer}
                            suffix=" %"
                            readonly={umsatzsteuerFixedValue !== undefined}
                        />
                    </Components.FormGroup>
                )}

                <Components.FormGroup label={t("rechnung.positionen.positionZeitraum.startdatum.label")}>
                    <Components.DateEdit modelPath={path + ".positionZeitraum.0.startdatum"} value={position?.positionZeitraum[0]?.startdatum} />
                </Components.FormGroup>
                <Components.FormGroup label={t("rechnung.positionen.positionZeitraum.enddatum.label")}>
                    <Components.DateEdit modelPath={path + ".positionZeitraum.0.enddatum"} value={position?.positionZeitraum[0]?.enddatum} />
                </Components.FormGroup>
                {!FeatureService.isFeatureActive("DEMO") && (
                    <>
                        <PositionSonstigeView path={path} position={position} onChange={onChange} />
                        <PositionZusatzinformationListView positionMetadaten={position.positionMetadaten} path={path + ".positionMetadaten"} />
                        <PositionListView
                            positionen={position.unterPositionen}
                            path={`${path}.unterPositionen`}
                            name={t("rechnung.positionen.unterPositionen.title")}
                            umsatzsteuerFixedValue={position.umsatzsteuer}
                            umsatzsteuerFixedCode={position.umsatzsteuerCode}
                            subPostion
                        />
                    </>
                )}
            </Components.Collapsible>
        </div>
    )
}
